import { escapeAttr, escapeHtml, linkAttrs, } from "@emkit/builder-core";
export const HERO_DEFAULTS = {
    eyebrow: "New",
    title: "Ship landing pages in minutes",
    subtitle: "EmBuilder compiles clean static HTML with zero runtime overhead — drop it on any host.",
    primaryCtaLabel: "Get started",
    primaryCtaHref: "#",
    primaryCtaVariant: "primary",
    secondaryCtaLabel: "See how it works",
    secondaryCtaHref: "#",
    secondaryCtaVariant: "ghost",
    imageUrl: "https://placehold.co/1200x900/e5e7eb/6b7280?text=Hero",
    imageAlt: "Product preview",
    layout: "image-right",
    size: "md",
    background: "#ffffff",
    textColor: "#0f172a",
    accent: "#111827",
};
const SIZES = {
    sm: { paddingY: 48, titleSize: 32, subtitleSize: 16, gap: 32 },
    md: { paddingY: 72, titleSize: 44, subtitleSize: 18, gap: 48 },
    lg: { paddingY: 96, titleSize: 56, subtitleSize: 20, gap: 64 },
};
const BUTTON_STYLES = {
    primary: (accent) => `background:${accent};color:#fff;border:1px solid ${accent}`,
    secondary: (accent, text) => `background:#fff;color:${text};border:1px solid ${accent}`,
    ghost: (_accent, text) => `background:transparent;color:${text};border:1px solid transparent;text-decoration:underline`,
};
function renderButton(label, href, variant, accent, textColor, target) {
    if (!label)
        return "";
    const style = [
        "display:inline-flex",
        "align-items:center",
        "justify-content:center",
        "padding:12px 22px",
        "border-radius:8px",
        "font-weight:500",
        "font-size:15px",
        "line-height:1.2",
        "text-decoration:none",
        "transition:opacity 0.15s",
        BUTTON_STYLES[variant](accent, textColor),
    ].join(";");
    const safeHref = href ?? "#";
    return `<a href="${escapeAttr(safeHref)}"${linkAttrs(safeHref, target)} class="emk-hero-btn" style="${style}">${escapeHtml(label)}</a>`;
}
export const heroWidget = {
    type: "hero",
    defaultProps: HERO_DEFAULTS,
    renderHtml(props, ctx) {
        const size = SIZES[props.size ?? "md"];
        const layout = props.layout ?? "image-right";
        const bg = props.background ?? "#ffffff";
        const text = props.textColor ?? "#0f172a";
        const accent = props.accent ?? "#111827";
        const imgUrl = props.imageUrl
            ? (ctx.resolveAssetUrl?.(props.imageUrl) ?? props.imageUrl)
            : "";
        const eyebrow = props.eyebrow
            ? `<div style="display:inline-block;padding:4px 10px;border-radius:999px;background:${accent}1a;color:${accent};font-size:13px;font-weight:500;margin-bottom:16px">${escapeHtml(props.eyebrow)}</div>`
            : "";
        const subtitle = props.subtitle
            ? `<p style="margin:16px 0 24px;font-size:${size.subtitleSize}px;line-height:1.55;color:${text};opacity:0.8;max-width:640px">${escapeHtml(props.subtitle)}</p>`
            : "";
        const ctas = [
            renderButton(props.primaryCtaLabel, props.primaryCtaHref, props.primaryCtaVariant ?? "primary", accent, text, props.primaryCtaTarget),
            renderButton(props.secondaryCtaLabel, props.secondaryCtaHref, props.secondaryCtaVariant ?? "ghost", accent, text, props.secondaryCtaTarget),
        ]
            .filter(Boolean)
            .join("");
        const ctaRow = ctas
            ? `<div class="emk-hero-ctas" style="display:flex;gap:12px;flex-wrap:wrap;margin-top:8px">${ctas}</div>`
            : "";
        const titleEl = `<h1 class="emk-hero-title" style="margin:0;font-size:${size.titleSize}px;line-height:1.15;font-weight:700;letter-spacing:-0.02em;color:${text}">${escapeHtml(props.title)}</h1>`;
        const copyBlock = `${eyebrow}${titleEl}${subtitle}${ctaRow}`;
        const containerStyle = [
            `background:${bg}`,
            `color:${text}`,
            `padding:${size.paddingY}px 20px`,
            "font-family:inherit",
        ].join(";");
        if (layout === "centered" || !imgUrl) {
            return `<section class="emk-hero emk-hero--centered" style="${containerStyle}"><div style="max-width:800px;margin:0 auto;text-align:center;display:flex;flex-direction:column;align-items:center;gap:0">${copyBlock}</div></section>`;
        }
        if (layout === "background") {
            return `<section class="emk-hero emk-hero--bg" style="${containerStyle};background-image:linear-gradient(rgba(0,0,0,0.35),rgba(0,0,0,0.55)),url('${escapeAttr(imgUrl)}');background-size:cover;background-position:center;color:#fff"><div style="max-width:800px;margin:0 auto;text-align:center;color:#fff">${copyBlock.replace(new RegExp(`color:${text}`, "g"), "color:#fff")}</div></section>`;
        }
        const imgEl = `<img src="${escapeAttr(imgUrl)}" alt="${escapeAttr(props.imageAlt ?? "")}" loading="eager" decoding="async" style="width:100%;height:auto;border-radius:12px;display:block">`;
        const order = layout === "image-left" ? "row-reverse" : "row";
        const grid = `display:grid;grid-template-columns:1fr 1fr;gap:${size.gap}px;align-items:center;max-width:1200px;margin:0 auto;direction:${order === "row-reverse" ? "rtl" : "ltr"}`;
        // Use CSS direction trick so source order stays accessible but visual order flips.
        return `<section class="emk-hero emk-hero--split" style="${containerStyle}"><div style="${grid}"><div style="direction:ltr">${copyBlock}</div><div style="direction:ltr">${imgEl}</div></div></section>`;
    },
};
//# sourceMappingURL=hero.js.map